import { createAppError } from '../../api/errors';
import {
  dashboardMetaFixture,
  dashboardMockFilterFixture,
  dashboardSummaryFixture,
  dashboardTopAffiliatesFixture,
  dashboardTopGamesFixture,
  dashboardTrendsFixture,
} from './fixtures';
import { createDefaultDashboardFilters, type DashboardRepository } from './repository';
import type {
  DashboardCurrencyTotals,
  DashboardFilters,
  DashboardRanking,
  DashboardRankingRow,
  DashboardSummary,
  DashboardTrendPoint,
  DashboardTrends,
} from './types';

export interface MockDashboardRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
}

export const defaultMockDashboardFilters = createDefaultDashboardFilters(
  new Date(dashboardMetaFixture.reportingAsOf),
);

export class MockDashboardRepository implements DashboardRepository {
  readonly #mode: 'data' | 'empty' | 'error';

  constructor(options: MockDashboardRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
  }

  getSummary(filters: DashboardFilters, signal: AbortSignal): Promise<DashboardSummary> {
    this.#assertAvailable(signal);
    const context = this.#mode === 'empty' ? null : filterContext(filters);
    return Promise.resolve(
      context === null ? emptySummary(filters) : filterSummary(filters, context),
    );
  }

  getTrends(filters: DashboardFilters, signal: AbortSignal): Promise<DashboardTrends> {
    this.#assertAvailable(signal);
    const context = this.#mode === 'empty' ? null : filterContext(filters);
    return Promise.resolve({
      ...dashboardTrendsFixture,
      points: context === null ? [] : filterTrends(filters, context),
      meta: filteredMeta(filters),
    });
  }

  getTopGames(filters: DashboardFilters, signal: AbortSignal): Promise<DashboardRanking> {
    this.#assertAvailable(signal);
    const context = this.#mode === 'empty' ? null : filterContext(filters);
    return Promise.resolve({
      ...dashboardTopGamesFixture,
      rows:
        context === null
          ? []
          : filterRanking(dashboardTopGamesFixture.rows, filters, context, true),
      meta: filteredMeta(filters),
    });
  }

  getTopAffiliates(filters: DashboardFilters, signal: AbortSignal): Promise<DashboardRanking> {
    this.#assertAvailable(signal);
    const context = this.#mode === 'empty' ? null : filterContext(filters);
    return Promise.resolve({
      ...dashboardTopAffiliatesFixture,
      rows:
        context === null
          ? []
          : filterRanking(dashboardTopAffiliatesFixture.rows, filters, context, false),
      meta: filteredMeta(filters),
    });
  }

  #assertAvailable(signal: AbortSignal): void {
    if (signal.aborted) throw new DOMException('Dashboard request aborted', 'AbortError');
    if (this.#mode === 'error') {
      throw createAppError({
        status: 503,
        code: 'MOCK_DASHBOARD_UNAVAILABLE',
        message: 'Dashboard fixture is unavailable',
        requestId: 'mock-dashboard-error',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
  }
}

interface FilterContext {
  readonly gameCurrency: string | null;
  readonly gameFactor: number;
  readonly points: readonly DashboardTrendPoint[];
  readonly statusFactor: number;
}

function filterContext(filters: DashboardFilters): FilterContext | null {
  if (!scopeMatches(filters)) return null;
  const statusFactor = transactionStatusFactor(filters.status);
  if (statusFactor === null) return null;

  const selectedGame =
    filters.gameId.length === 0
      ? null
      : dashboardTopGamesFixture.rows.find((row) => row.id === filters.gameId);
  if (filters.gameId.length > 0 && selectedGame === undefined) return null;
  if (
    selectedGame !== null &&
    selectedGame !== undefined &&
    filters.currency.length > 0 &&
    selectedGame.currencyCode !== filters.currency
  ) {
    return null;
  }

  const gameCurrency = selectedGame?.currencyCode ?? null;
  const points = dashboardTrendsFixture.points.filter(
    (point) =>
      inDateRange(point, filters) &&
      (filters.currency.length === 0 || point.currencyCode === filters.currency) &&
      (gameCurrency === null || point.currencyCode === gameCurrency),
  );
  if (points.length === 0) return null;

  return {
    gameCurrency,
    gameFactor: selectedGame === null || selectedGame === undefined ? 1 : gameShare(selectedGame),
    points,
    statusFactor,
  };
}

function filterSummary(filters: DashboardFilters, context: FilterContext): DashboardSummary {
  const totalFactor = overallTrendShare(context.points) * context.gameFactor;
  const statusCounts = filteredStatusCounts(filters.status, totalFactor);
  const currencies = dashboardSummaryFixture.currencies
    .filter(
      (currency) =>
        (filters.currency.length === 0 || currency.currencyCode === filters.currency) &&
        (context.gameCurrency === null || currency.currencyCode === context.gameCurrency),
    )
    .map((currency) =>
      scaleCurrency(
        currency,
        currencyTrendShare(context.points, currency.currencyCode) *
          context.gameFactor *
          context.statusFactor,
      ),
    );

  return {
    ...dashboardSummaryFixture,
    ...statusCounts,
    activeUserCount: scaleCount(dashboardSummaryFixture.activeUserCount, totalFactor),
    registrationCount: scaleCount(dashboardSummaryFixture.registrationCount, totalFactor),
    withdrawalExceptionCount: scaleCount(
      dashboardSummaryFixture.withdrawalExceptionCount,
      totalFactor,
    ),
    currencies,
    meta: filteredMeta(filters),
  };
}

function filterTrends(
  filters: DashboardFilters,
  context: FilterContext,
): readonly DashboardTrendPoint[] {
  return context.points.map((point) => {
    const factor = context.gameFactor * context.statusFactor;
    return {
      ...point,
      transactionCount: scaleCount(point.transactionCount, factor),
      registrationCount: scaleCount(point.registrationCount, context.gameFactor),
      playAmount: scaleDecimal(point.playAmount, factor),
      agentCommission: scaleDecimal(point.agentCommission, factor),
      affiliateCommission: scaleDecimal(point.affiliateCommission, factor),
      completedWithdrawalAmount: scaleDecimal(point.completedWithdrawalAmount, factor),
    };
  });
}

function filterRanking(
  rows: readonly DashboardRankingRow[],
  filters: DashboardFilters,
  context: FilterContext,
  games: boolean,
): readonly DashboardRankingRow[] {
  return rows
    .filter(
      (row) =>
        (filters.currency.length === 0 || row.currencyCode === filters.currency) &&
        (context.gameCurrency === null || row.currencyCode === context.gameCurrency) &&
        (!games || filters.gameId.length === 0 || row.id === filters.gameId),
    )
    .map((row) => {
      const dateFactor = currencyTrendShare(context.points, row.currencyCode);
      const gameFactor = games ? 1 : context.gameFactor;
      const factor = dateFactor * gameFactor * context.statusFactor;
      return {
        ...row,
        transactionCount: scaleCount(row.transactionCount, factor),
        playAmount: scaleDecimal(row.playAmount, factor),
        totalCommission: scaleDecimal(row.totalCommission, factor),
      };
    });
}

function emptySummary(filters: DashboardFilters): DashboardSummary {
  return {
    ...dashboardSummaryFixture,
    transactionCount: 0,
    calculatedTransactionCount: 0,
    rejectedTransactionCount: 0,
    failedTransactionCount: 0,
    activeUserCount: 0,
    registrationCount: 0,
    withdrawalExceptionCount: 0,
    currencies: [],
    apiHealth: null,
    meta: filteredMeta(filters),
  };
}

function scopeMatches(filters: DashboardFilters): boolean {
  return (
    matches(filters.partnerId, dashboardMockFilterFixture.partnerId) &&
    matches(filters.agentId, dashboardMockFilterFixture.agentId) &&
    matches(filters.gameTypeId, dashboardMockFilterFixture.gameTypeId)
  );
}

function matches(filter: string, fixture: string): boolean {
  return filter.length === 0 || filter === fixture;
}

function inDateRange(point: DashboardTrendPoint, filters: DashboardFilters): boolean {
  const pointTime = new Date(point.periodStart).getTime();
  const from = new Date(filters.from).getTime();
  const to = new Date(filters.to).getTime();
  return Number.isFinite(from) && Number.isFinite(to) && pointTime >= from && pointTime < to;
}

function transactionStatusFactor(status: string): number | null {
  if (status.length === 0) return 1;
  const count = statusCount(status);
  return count === null ? null : count / dashboardSummaryFixture.transactionCount;
}

function filteredStatusCounts(status: string, factor: number) {
  if (status.length === 0) {
    return {
      transactionCount: scaleCount(dashboardSummaryFixture.transactionCount, factor),
      calculatedTransactionCount: scaleCount(
        dashboardSummaryFixture.calculatedTransactionCount,
        factor,
      ),
      rejectedTransactionCount: scaleCount(
        dashboardSummaryFixture.rejectedTransactionCount,
        factor,
      ),
      failedTransactionCount: scaleCount(dashboardSummaryFixture.failedTransactionCount, factor),
    };
  }
  const transactionCount = scaleCount(statusCount(status) ?? 0, factor);
  return {
    transactionCount,
    calculatedTransactionCount: status === 'CALCULATED' ? transactionCount : 0,
    rejectedTransactionCount: status === 'REJECTED' ? transactionCount : 0,
    failedTransactionCount: status === 'FAILED' ? transactionCount : 0,
  };
}

function statusCount(status: string): number | null {
  if (status === 'CALCULATED') return dashboardSummaryFixture.calculatedTransactionCount;
  if (status === 'REJECTED') return dashboardSummaryFixture.rejectedTransactionCount;
  if (status === 'FAILED') return dashboardSummaryFixture.failedTransactionCount;
  return null;
}

function overallTrendShare(points: readonly DashboardTrendPoint[]): number {
  return transactionTotal(points) / transactionTotal(dashboardTrendsFixture.points);
}

function currencyTrendShare(points: readonly DashboardTrendPoint[], currencyCode: string): number {
  const selected = points.filter((point) => point.currencyCode === currencyCode);
  const all = dashboardTrendsFixture.points.filter((point) => point.currencyCode === currencyCode);
  return transactionTotal(selected) / transactionTotal(all);
}

function transactionTotal(points: readonly DashboardTrendPoint[]): number {
  return points.reduce((total, point) => total + point.transactionCount, 0);
}

function gameShare(game: DashboardRankingRow): number {
  const currencyTotal = dashboardTopGamesFixture.rows
    .filter((row) => row.currencyCode === game.currencyCode)
    .reduce((total, row) => total + Number(row.playAmount), 0);
  return Number(game.playAmount) / currencyTotal;
}

function scaleCurrency(currency: DashboardCurrencyTotals, factor: number): DashboardCurrencyTotals {
  return {
    currencyCode: currency.currencyCode,
    playAmount: scaleDecimal(currency.playAmount, factor),
    agentCommissionGross: scaleDecimal(currency.agentCommissionGross, factor),
    affiliateCommissionGross: scaleDecimal(currency.affiliateCommissionGross, factor),
    commissionReversals: scaleDecimal(currency.commissionReversals, factor),
    commissionAdjustments: scaleDecimal(currency.commissionAdjustments, factor),
    netCommission: scaleDecimal(currency.netCommission, factor),
    walletAvailable: scaleDecimal(currency.walletAvailable, factor),
    walletReserved: scaleDecimal(currency.walletReserved, factor),
    withdrawalRequested: scaleDecimal(currency.withdrawalRequested, factor),
    withdrawalReserved: scaleDecimal(currency.withdrawalReserved, factor),
    withdrawalCompleted: scaleDecimal(currency.withdrawalCompleted, factor),
    withdrawalReleased: scaleDecimal(currency.withdrawalReleased, factor),
    withdrawalUnknown: scaleDecimal(currency.withdrawalUnknown, factor),
  };
}

function filteredMeta(filters: DashboardFilters) {
  return {
    ...dashboardTrendsFixture.meta,
    normalizedFilters: Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value.length > 0),
    ),
  };
}

function scaleCount(value: number, factor: number): number {
  return Math.max(0, Math.round(value * factor));
}

function scaleDecimal(value: string, factor: number): string {
  return (Number(value) * factor).toFixed(6);
}
